import type { LendingMode, LenderYields, BalanceData, AprData, LoopPostTradeMetrics } from '../types'
import { noOpResult, buildLoopResult } from '../utils'
import {
  getOrganicYieldOpen,
  getRewardYieldOpen,
  getIntrinsicYieldOpen,
} from '../yield'

/**
 * Compute post-trade balance & APR metrics for opening a leveraged position.
 *
 * Borrows `dollarIn` of the source asset and deposits `dollarOut` of the target asset.
 */
export function computeOpenTradeDeltas(
  dollarIn: number,
  dollarOut: number,
  sourceMode: LendingMode,
  yieldParamsIn: LenderYields,
  yieldParamsOut: LenderYields,
  balance: BalanceData,
  apr: AprData,
  bfIn: number,
  collateralLtvOut: number,
  borrowLtvOut: number,
  useAllActive = false
): LoopPostTradeMetrics {
  if (dollarIn === 0 || dollarOut === 0) return noOpResult(balance, apr)

  const cashFlowDeposits = balance.deposits * apr.depositApr
  const cashFlowDebt = balance.debt * apr.borrowApr
  const rewardFlowDeposits = balance.deposits * (apr.rewardDepositApr ?? 0)
  const rewardFlowDebt = balance.debt * (apr.rewardBorrowApr ?? 0)
  const intrinsicFlowDeposits = balance.deposits * apr.intrinsicDepositApr
  const intrinsicFlowDebt = balance.debt * apr.intrinsicBorrowApr

  const newBalance = { ...balance }
  const newApr = { ...apr }

  newBalance.debt += dollarIn
  newBalance.borrowDiscountedCollateral += borrowLtvOut * dollarOut
  newBalance.borrowDiscountedCollateralAllActive += borrowLtvOut * dollarOut
  newBalance.collateral += collateralLtvOut * dollarOut
  newBalance.collateralAllActive += collateralLtvOut * dollarOut
  newBalance.deposits += dollarOut
  newBalance.adjustedDebt += bfIn * dollarIn
  newBalance.nav += dollarOut - dollarIn

  ;[newApr.borrowApr, newApr.depositApr, newApr.apr] = getOrganicYieldOpen(
    dollarIn, dollarOut, sourceMode,
    cashFlowDeposits, cashFlowDebt,
    balance.deposits, balance.debt,
    yieldParamsIn, yieldParamsOut
  )
  newApr.rewards = getRewardYieldOpen(
    dollarIn, dollarOut, sourceMode,
    rewardFlowDeposits, rewardFlowDebt,
    balance.deposits, balance.debt,
    yieldParamsIn, yieldParamsOut
  )
  ;[newApr.intrinsicBorrowApr, newApr.intrinsicDepositApr, newApr.intrinsicApr] =
    getIntrinsicYieldOpen(
      dollarIn, dollarOut,
      intrinsicFlowDeposits, intrinsicFlowDebt,
      balance.deposits, balance.debt,
      yieldParamsIn, yieldParamsOut
    )

  return buildLoopResult(balance, newBalance, apr, newApr, useAllActive)
}
